import { format, parseISO } from 'date-fns';
import type { ISchedulerTask } from '@interfaces/IScheduler';

// ---------------------------------------------------------------------------
// The plan window as a label. Two surfaces render it — the drawer header and
// the Overview's sub-task rows — at two densities, so the milestone rule ("a
// milestone is a point in time, not a range") and the empty-date guard live
// here rather than once per caller.
//
// A bare `YYYY-MM-DD` is already zone-free: it is formatted as written, never
// anchored to an instant and re-read through a zone (frontend CLAUDE.md §6).
// ---------------------------------------------------------------------------

/** Placeholder for a date the plan does not carry. */
const UNKNOWN = '—';

/** One day. `''` is the codec's documented "unknown date" sentinel — guarded
 *  so it renders as a dash rather than as an Invalid Date. */
export function formatDay(iso: string, pattern: string): string {
  if (!iso) return UNKNOWN;
  const parsed = parseISO(iso);
  return Number.isNaN(parsed.getTime()) ? UNKNOWN : format(parsed, pattern);
}

/** `start → end`, or the single day a milestone stands on. */
export function taskWindowLabel(
  task: Pick<ISchedulerTask, 'start' | 'end' | 'isMilestone'>,
  pattern = 'yyyy/MM/dd',
): string {
  const start = formatDay(task.start, pattern);
  return task.isMilestone ? start : `${start} → ${formatDay(task.end, pattern)}`;
}

// ---------------------------------------------------------------------------
// The one date-validity rule. A plan day is a real `YYYY-MM-DD` calendar day;
// a task is SCHEDULED when both of its days are. The drawer's Save gate and
// the plan reducers ask the same question, so an empty or half-typed date can
// never reach the date maths (which throws on an invalid day).
//
// An imported row may legitimately carry no dates (`''`, the codec's unknown
// sentinel): it is an UNSCHEDULED row — listed, but with no bar, no span in
// its summary, and no part in the dependency cascade.
// ---------------------------------------------------------------------------

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day in `YYYY-MM-DD` form (`2026-02-30` is not one). Cheap
 *  enough for the per-task hot paths (bounds, roll-ups, the cascade). */
export function isPlanDay(value: string): boolean {
  const match = DAY_PATTERN.exec(value);
  if (!match) return false;
  const [, year, month, day] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Both days present and real — the task has a place on the timeline. */
export function isScheduled(task: Pick<ISchedulerTask, 'start' | 'end'>): boolean {
  return isPlanDay(task.start) && isPlanDay(task.end);
}

export type TaskDatesProblem = 'missing' | 'endBeforeStart';

/** What stops these dates from being saved, or `null` when they may be. */
export function taskDatesProblem(task: Pick<ISchedulerTask, 'start' | 'end'>): TaskDatesProblem | null {
  if (!isScheduled(task)) return 'missing';
  return task.end < task.start ? 'endBeforeStart' : null;
}
